import React from 'react'
import { 
  ExclamationTriangleIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
  EyeSlashIcon,
  ScaleIcon,
  CurrencyRupeeIcon,
  BuildingLibraryIcon,
  MegaphoneIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'

const RiskDisclosurePage: React.FC = () => {
  const sections = [
    {
      icon: InformationCircleIcon,
      title: '1. Purpose & Scope',
      content: [
        'This document is prepared in compliance with the SEBI (Research Analysts) Regulations, 2014 to enable prospective clients/users of research/recommendation services to make an informed decision',
        'The information, views, and recommendations provided by Analyzerview Market Research are of a general nature and do not consider the particular investment objectives, financial situation, or risk profile of any individual.'
      ]
    },
    {
      icon: DocumentTextIcon,
      title: '2. Disclosure of Information Sources & Accuracy',
      content: [
        'Our research is based on public information, internal analysis, and sources we believe to be reliable. However, we do not warrant or guarantee their accuracy, adequacy, or completeness.',
        'Opinions and estimates are subject to change without notice. Analyzerview Market Research is under no obligation to update or revise the information/recommendations.'
      ]
    },
    {
      icon: ExclamationTriangleIcon,
      title: '3. Risk Factors',
      content: [
        'Investment in securities involves risks. The value of investments may go up or down, and investors may lose some or all of their investment. Past performance is not indicative of future results.',
        'The suitability of any recommendation depends on the user\'s specific financial position, objectives, and risk tolerance. Before making investment decisions, users should consult their own financial, tax, or legal advisers.'
      ]
    },
    {
      icon: EyeSlashIcon,
      title: '4. Conflict of Interest & Other Disclosures',
      content: [
        'Analyzerview Market Research, its analysts, or its associates/relatives may have financial or other interests (including beneficial ownership) in securities mentioned in our reports. If so, we will disclose them clearly in individual reports.',
        'We will disclose whether any remuneration or benefit has been received from the companies whose securities are recommended, or from third parties, in the past 12 months.',
        'Analyzerview Market Research or its affiliates have not engaged in market-making activity for the subject company unless disclosed.'
      ]
    },
    {
      icon: ShieldExclamationIcon,
      title: '5. Limitation of Liability & Use of Reports / Services',
      content: [
        'The reports, research, communications, or recommendations issued are for information purposes only, and are not to be taken as a solicitation or offer to buy/sell any security.',
        'Users/clients act on the information at their own risk. Analyzerview Market Research, its directors, employees, or associates shall not be liable for any loss or damage arising directly or indirectly from use of this information.',
        'Unauthorized reproduction, redistribution, or sharing of our reports or recommendation content is prohibited.'
      ]
    },
    {
      icon: BuildingLibraryIcon,
      title: '6. Regulatory & Compliance Disclaimers',
      content: [
        'Registration granted by SEBI and certification from NISM do not guarantee performance of the Research Analyst or provide assurance of returns.',
        'Analyzerview Market Research complies with all requirements under SEBI (Research Analysts) Regulations, 2014, including disclosure and mitigation of conflicts, maintaining records, reporting disciplinary history, etc. If any material disciplinary action has been taken by any regulatory authority, it will be disclosed.'
      ]
    },
    {
      icon: CurrencyRupeeIcon,
      title: '7. Client Rights, Fees & Terms',
      content: [
        'Client subscribing to the research services confirms having read, understood, and accepted the terms & conditions applicable under SEBI RA Regulations, including the fee structure.',
        'The fee charged will be disclosed, and paid by accepted modes as per SEBI / RAASB guidelines.',
        'In event of cancellation or suspension of registration by SEBI, or non-performance by Analyzerview Market Research, residual fees will be refunded pro rata, if applicable.'
      ]
    },
    {
      icon: ScaleIcon,
      title: '8. Jurisdiction and Governing Law',
      content: [
        'This Disclosure & Disclaimer, and all research reports or services by Analyzerview Market Research are governed by Indian law. Disputes arising from or in connection with these shall be subject to the exclusive jurisdiction of the courts of Kalna, West Bengal.'
      ]
    },
    {
      icon: MegaphoneIcon,
      title: '9. Standard Warning',
      content: [
        '"Investments in securities market are subject to market risk. Read all the related documents carefully before investing. Registration granted by SEBI and certification from NISM in no way guarantee performance of the Research Analyst or provide any assurance of returns to investors."'
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <ExclamationTriangleIcon aria-hidden="true" focusable="false" className="w-16 h-16 mx-auto mb-6 text-red-200" />
            <h1 className="text-3xl md:text-5xl font-bold mb-6 text-red-300">
              Risk Disclosure & Disclaimer
            </h1>
            <div className="bg-red-700/30 rounded-lg p-6 max-w-4xl mx-auto mb-6">
              <p className="text-lg md:text-xl font-semibold mb-2 text-red-100">
                SEBI Registered Research Analyst Registration No.: INH000023719
              </p>
              <p className="text-base md:text-lg text-red-100">
                Name of Research Entity: Analyzerview Market Research
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-lg text-gray-300 leading-relaxed">
              This comprehensive disclosure document is prepared in compliance with SEBI regulations 
              to ensure that all clients and users of our research services are fully informed about 
              the risks, limitations, and terms associated with our recommendations and research reports.
            </p>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="sr-only">Risk Disclosure Sections</h2>
          <ul className="space-y-8 list-none">
            {sections.map((section, index) => (
              <li key={index}>
                <Card className="overflow-hidden">
                  <div className="flex flex-col lg:flex-row">
                    <div className="lg:w-1/4 bg-gray-700 p-6 flex flex-col items-center lg:items-start text-center lg:text-left">
                      <div className="w-12 h-12 bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                        <section.icon aria-hidden="true" focusable="false" className="w-6 h-6 text-red-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">
                        {section.title}
                      </h3>
                    </div>
                    <div className="lg:w-3/4 p-6">
                      <div className="space-y-4">
                        {section.content.map((paragraph, pIndex) => (
                          <p key={pIndex} className="text-gray-300 leading-relaxed">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Important Notice Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-red-700 bg-gray-900">
            <div className="text-center">
              <ExclamationTriangleIcon aria-hidden="true" focusable="false" className="w-16 h-16 text-red-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-white mb-6">
                Important Notice
              </h2>
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-6 mb-6">
                <p className="text-lg font-semibold text-red-200 mb-4">
                  "Investments in securities market are subject to market risk. Read all the related documents carefully before investing."
                </p>
                <p className="text-sm text-red-300">
                  Registration granted by SEBI and certification from NISM in no way guarantee performance of the Research Analyst or provide any assurance of returns to investors.
                </p>
              </div>
              <p className="text-gray-300 leading-relaxed">
                By using our research services, you acknowledge that you have read, understood, and agree to be bound by all the terms, conditions, and disclaimers outlined in this document. Please consult with qualified financial advisors before making any investment decisions.
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Questions About Risk Disclosures?
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            If you need clarification on any aspect of these risk disclosures or have questions 
            about our research services, please contact our compliance team.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
          >
            Contact Compliance Team
          </a>
        </div>
      </section>
    </div>
  )
}

export default RiskDisclosurePage